/**
 * Content Packages list page component.
 *
 * @module pages/ContentPackagesPage
 */

import { useState, useRef } from 'react';
import { useParams } from 'react-router';
import { Package, Trash2, Upload, Plus } from 'lucide-react';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import {
  useContentPackages,
  useDeleteContentPackage,
  useCreateContentPackage,
  contentPackageKeys,
  sandboxGroupKeys,
} from '@/hooks';
import { contentPackageService } from '@/services';
import { useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import type { ContentPackageResponse } from '@/types';

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Content packages list page
 */
export function ContentPackagesPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const { data: contentPackages, isLoading } = useContentPackages(groupId);
  const deleteMutation = useDeleteContentPackage();
  const createMutation = useCreateContentPackage();

  const [deleteTarget, setDeleteTarget] = useState<ContentPackageResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLabels, setCreateLabels] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(
        { id: deleteTarget.id, sandboxGroupId: groupId },
        {
          onSuccess: () => setDeleteTarget(null),
        }
      );
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && contentPackages) {
      setSelectedIds(new Set(contentPackages.map((pkg) => pkg.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map(async (id) => {
          try {
            await contentPackageService.delete(id, groupId);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete content package ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    queryClient.invalidateQueries({
      queryKey: groupId ? sandboxGroupKeys.contentPackages(groupId) : contentPackageKeys.list(),
    });

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setIsBulkDeleting(false);
    setSelectedIds(new Set());

    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Parse labels from string
      const labels: Record<string, string> = {};
      if (createLabels.trim()) {
        createLabels.split(',').forEach((pair) => {
          const [key, value] = pair.split('=').map((s) => s.trim());
          if (key && value) {
            labels[key] = value;
          }
        });
      }

      createMutation.mutate(
        {
          file,
          labels: Object.keys(labels).length > 0 ? labels : undefined,
          sandboxGroupId: groupId,
        },
        {
          onSuccess: () => {
            setShowCreateModal(false);
            setCreateLabels('');
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          },
        }
      );
    }
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Content Packages</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage binary content packages that can be attached to sandboxes.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          <Plus className="h-4 w-4" />
          Upload Package
        </button>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
          <span className="text-sm text-blue-700 dark:text-blue-300">
            {selectedIds.size} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-blue-700 dark:text-blue-300 hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      {contentPackages && contentPackages.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-[#2a2a2a]">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th scope="col" className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === contentPackages.length && contentPackages.length > 0
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Labels
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Size
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Content Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                >
                  Created
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-[#2a2a2a] dark:bg-[#161616]">
              {contentPackages.map((pkg) => (
                <tr key={pkg.id} className="hover:bg-gray-50 dark:hover:bg-[#1a1a1a]">
                  <td className="w-12 px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(pkg.id)}
                      onChange={(e) => handleSelectOne(pkg.id, e.target.checked)}
                      className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Package className="h-5 w-5 text-gray-400" />
                      <span className="font-mono text-sm text-gray-900 dark:text-white">
                        {pkg.id}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(pkg.labels || {}).map(([key, value]) => (
                        <span
                          key={key}
                          className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        >
                          {key}={value}
                        </span>
                      ))}
                      {(!pkg.labels || Object.keys(pkg.labels).length === 0) && (
                        <span className="text-sm text-gray-400">No labels</span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatBytes(pkg.size)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {pkg.contentType || <span className="text-gray-400 italic">Unknown</span>}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {pkg.createdOn
                      ? formatDistanceToNow(new Date(pkg.createdOn), { addSuffix: true })
                      : '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                    <button
                      onClick={() => setDeleteTarget(pkg)}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-12 dark:border-gray-700 dark:bg-[#1a1a1a]">
          <Package className="h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No content packages
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Get started by uploading a content package.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Upload className="h-4 w-4" />
            Upload Package
          </button>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Content Package"
        message={`Are you sure you want to delete content package "${deleteTarget?.id}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Bulk delete modal */}
      <ConfirmModal
        isOpen={showBulkDeleteModal}
        onClose={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
        onConfirm={handleBulkDelete}
        title="Delete Content Packages"
        message={
          isBulkDeleting
            ? `Deleting ${bulkDeleteProgress.current}/${bulkDeleteProgress.total}...`
            : `Are you sure you want to delete ${selectedIds.size} content packages? This action cannot be undone.`
        }
        confirmText={isBulkDeleting ? 'Deleting...' : 'Delete All'}
        variant="danger"
        isLoading={isBulkDeleting}
      />

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-[#1a1a1a]">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Upload Content Package
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Upload a binary file to create a content package.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Labels (optional)
                </label>
                <input
                  type="text"
                  value={createLabels}
                  onChange={(e) => setCreateLabels(e.target.value)}
                  placeholder="key1=value1,key2=value2"
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-hidden focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-[#161616] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  File
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  disabled={createMutation.isPending}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-gray-400 dark:file:bg-blue-900/50 dark:file:text-blue-300"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateLabels('');
                }}
                disabled={createMutation.isPending}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
